import { Component, Renderer2, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantWhitepaperComponent } from '../relevant-whitepaper/relevant-whitepaper.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ResourcesService } from '../../../services/resources.service';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-seatlayout-workflow',
  imports: [CommonModule, RelevantWhitepaperComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './seatlayout-workflow.component.html',
  styleUrl: './seatlayout-workflow.component.scss'
})
export class SeatlayoutWorkflowComponent implements OnInit, OnDestroy {
  isModalOpen: boolean = false;
  downloadForm: FormGroup;
  isSubmitting: boolean = false;
  submitSuccess: boolean = false;
  errorMessage: string = '';
  private jsonLdScript: any;

  // IMPORTANT: This string matches the Key added to the Dictionary in EmailService.cs
  readonly whitepaperTitle = "Whitepaper on Seat Layout Workflow for Movie Ticket Booking Mobile App";

  // Path for direct browser download (optional, if you want them to get it immediately + email)
  readonly pdfPath = "../../../../assets/resources/whitepaper/seatlayoutTicketbooking/SeatLayoutWorkflow.pdf";

  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+( [A-Za-z]+)*$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const seatLayoutSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/white-paper/seatlayout-workflow",
          "url": "https://www.kalpitatechnologies.com/white-paper/seatlayout-workflow",
          "name": "Whitepaper on Seat Layout Workflow for Movie Ticket Booking Mobile App — Kalpita Technologies",
          "description": "A technical white paper on designing and implementing dynamic seat layout workflows for movie ticket booking apps on iOS and Android.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/white-paper/seatlayout-workflow#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper", "name": "White Papers" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper/seatlayout-workflow", "name": "Seat Layout Workflow" } }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Whitepaper on Seat Layout Workflow for Movie Ticket Booking Mobile App",
          "articleBody": "This white paper provides a comprehensive step-by-step guide to designing and implementing a dynamic seat layout workflow for movie ticket booking mobile applications on both iOS and Android platforms...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Build a Dynamic Seat Layout for Movie Ticket Booking Apps",
          "description": "Step-by-step guide to designing and implementing a scalable seat layout workflow.",
          "step": [
            { "@type": "HowToStep", "name": "Set Up Scrollable Container", "text": "UIScrollView for iOS or HorizontalScrollView for Android." },
            { "@type": "HowToStep", "name": "Create Reusable Components", "text": "Build Custom Category, Row, and Seat views." },
            { "@type": "HowToStep", "name": "Integrate Backend", "text": "Fetch seat availability via REST APIs." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What platforms does the seat layout workflow support?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The seat layout workflow is designed for both iOS (Swift/Xcode) and Android (Kotlin/XML) mobile platforms."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(seatLayoutSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      // Construct Payload matching C# WhitepaperDownloadForm
      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        WhitepaperTitle: this.whitepaperTitle
      };

      this.resourcesService.sendWhitepaperEmail(payload).subscribe({
        next: (response: any) => {
          // Check backend response structure. Usually returns { IsError: false, data: true }
          if (response && !response.IsError) {
            this.handleSuccess();
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message || 'Failed to send email. Please try again.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error sending email:', error);
          this.errorMessage = 'Server error. Please check your internet connection.';
        }
      });
    } else {
      this.downloadForm.markAllAsTouched();
    }
  }

  handleSuccess() {
    this.isSubmitting = false;
    this.submitSuccess = true;

    // OPTIONAL: Trigger automatic browser download alongside email
    // this.triggerFileDownload(); 

    // Close modal after 4 seconds
    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  triggerFileDownload() {
    const link = document.createElement('a');
    link.href = this.pdfPath;
    link.download = 'rise-of-ai-agent.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
